import React, { useRef, useState } from "react";

/**
 * 14030 + 14033 — "Diligent Model" / "Custom Model" info badge + hover tooltip.
 *
 * Body of the tooltip describes the verbatim Airtable rubric — formulas live as
 * formula fields on the Questions and Sections tables in the Diligent Risk
 * base. Nothing in this copy is invented FE-side.
 *
 * 14033 AC #4 — the popover MUST cover:
 *   1. How the Risk Score works (intro)
 *   2. VQQ Risk Score (0-100%) — Green ≥80, Yellow 50-79, Red <50; "higher is better"
 *   3. Question Criticality — Critical / Major / Minor + one-line meanings
 *   4. Section Criticality — Critical / Major / Minor via bucketed mean of
 *      Question Criticality Numeric (5 / 3 / 1). Mean ≥ 4 Critical, >2 & <4
 *      Major, ≤2 Minor.
 *   5. Finding Severity — Critical / High / Medium / Low / Compliant / Not Scored
 *      chips + admin-review note
 *   6. Active model footnote — Standard vs Custom variant wording
 *
 * Hover behaviour: a small close-delay keeps the tooltip alive while the
 * cursor crosses the gap between the trigger button and the popover body.
 */
const DiligentModelTooltip = ({ alignRight = false, modelKind = "Standard", modelName = null }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  const isCustom = String(modelKind).toLowerCase() === "custom";
  const badgeLabel = isCustom ? "Custom Model" : "Diligent Model";
  const badgeClass = isCustom
    ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
    : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100";

  return (
    <span className="relative inline-flex items-start">
      <button
        type="button"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${badgeClass}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <InfoCircle className="h-3.5 w-3.5" />
        <span>{badgeLabel}</span>
      </button>

      {open ? (
        <div
          role="tooltip"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          className={`absolute z-30 top-full pt-2 ${alignRight ? "right-0" : "left-0"}`}
        >
          <div className="w-[32rem] max-w-[92vw] rounded-lg border border-gray-200 bg-white p-4 shadow-xl text-left">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              How the Risk Score works
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed">
              Every VQQ question carries a Diligent rubric triple — <strong>Impact</strong>,
              <strong> Likelihood</strong>, <strong>Detectability</strong>, each 1–5 — set
              in Airtable. Those three numbers feed the formulas below.
            </p>

            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
              <ModelPill title="Impact" body="Severity if the requirement fails." />
              <ModelPill title="Likelihood" body="Probability of the failure occurring." />
              <ModelPill
                title="Detectability"
                body="How easily we'd catch the failure. Inverted: higher = harder."
              />
            </div>

            <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-[11px] leading-relaxed text-gray-700">
              <div className="font-semibold text-gray-900">Question Risk Score</div>
              <div>= Impact × Likelihood × Detectability</div>
              <div className="text-gray-500 mt-0.5">range 1 – 125</div>
            </div>

            <Section title="Question RAG">
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Band band="Red" range="Risk Score ≥ 50" />
                <Band band="Yellow" range="20 – 49" />
                <Band band="Green" range="< 20" />
              </div>
            </Section>

            <Section title="VQQ Risk Score (0 – 100%)">
              <p className="text-xs text-gray-600 leading-relaxed">
                <span className="font-mono">
                  score = (1 − Σ realized risk / Σ total intrinsic risk) × 100
                </span>
                <br />
                Realized risk = full Question Risk Score on every non-Compliant finding,
                0 on Compliant; Not Scored findings are excluded from both sums.
              </p>
              <div className="flex flex-wrap gap-2 text-[11px] mt-2">
                <Band band="Green" range="≥ 80%" />
                <Band band="Yellow" range="50 – 79%" />
                <Band band="Red" range="< 50%" />
              </div>
              <p className="text-[11px] text-gray-500 italic mt-1">
                Higher % is better — it means less unmitigated risk.
              </p>
            </Section>

            <Section title="Question Criticality">
              <ul className="space-y-1 text-xs text-gray-600 leading-relaxed">
                <li>
                  <strong className="text-red-700">Critical</strong> — Risk Score ≥ 50 OR Impact ≥ 4. Demands SME priority review and a documented gap.
                </li>
                <li>
                  <strong className="text-orange-700">Major</strong> — 20 ≤ Risk Score &lt; 50 OR Impact = 3. Worth reviewing; gap-fixable.
                </li>
                <li>
                  <strong className="text-gray-700">Minor</strong> — Everything else; lowest priority.
                </li>
              </ul>
            </Section>

            <Section title="Section Criticality">
              <p className="text-xs text-gray-600 leading-relaxed">
                Bucketed mean of per-question Criticality Numeric values across the
                section (Critical = 5, Major = 3, Minor = 1). Mean ≥ 4 → Critical,
                &gt; 2 and &lt; 4 → Major, ≤ 2 → Minor.
              </p>
              <p className="text-[11px] text-gray-500 italic mt-1">
                Different from <em>Section Risk Score</em> (1–125, rubric-derived). The two answer
                related but distinct questions and can disagree without contradiction.
              </p>
            </Section>

            <Section title="Finding Severity (per vendor response)">
              <div className="flex flex-wrap gap-1.5 text-[11px]">
                <SeverityChip tier="Critical" className="bg-red-100 text-red-800 border-red-200" />
                <SeverityChip tier="High" className="bg-orange-100 text-orange-800 border-orange-200" />
                <SeverityChip tier="Medium" className="bg-yellow-100 text-yellow-800 border-yellow-200" />
                <SeverityChip tier="Low" className="bg-blue-100 text-blue-800 border-blue-200" />
                <SeverityChip tier="Compliant" className="bg-green-100 text-green-800 border-green-200" />
                <SeverityChip tier="Not Scored" className="bg-gray-100 text-gray-700 border-gray-200" />
              </div>
              <p className="text-[11px] text-gray-500 italic mt-1.5">
                A Diligent admin reviews every finding before the snapshot is published to sponsor and vendor.
              </p>
            </Section>

            <div className="mt-3 border-t border-gray-100 pt-2">
              <p className="text-[11px] text-gray-500 leading-relaxed">
                {isCustom ? (
                  <>
                    <strong className="text-gray-700">
                      {modelName || "Custom risk model"}
                    </strong>{" "}
                    — a sponsor-tuned variant of the Diligent Risk Model for this
                    template. Same scoring approach, with the sponsor&apos;s own weighting
                    adjustments on a subset of questions.
                  </>
                ) : (
                  <>
                    The standard Diligent Risk Model for this template. The same model
                    is used for every vendor responding to this template.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </span>
  );
};

const Section = ({ title, children }) => (
  <div className="mt-3">
    <h5 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">{title}</h5>
    <div className="mt-1.5">{children}</div>
  </div>
);

const ModelPill = ({ title, body }) => (
  <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
      {title}
    </div>
    <div className="text-[11px] text-gray-600 leading-snug mt-0.5">{body}</div>
  </div>
);

const BAND_CLASS = {
  Green: "bg-green-100 text-green-800 border-green-200",
  Yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Red: "bg-red-100 text-red-800 border-red-200",
};

const Band = ({ band, range }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${BAND_CLASS[band]}`}
  >
    <span>{band}</span>
    <span className="opacity-70">·</span>
    <span className="tabular-nums">{range}</span>
  </span>
);

const SeverityChip = ({ tier, className }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${className}`}>
    {tier}
  </span>
);

const InfoCircle = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z"
      clipRule="evenodd"
    />
  </svg>
);

export default DiligentModelTooltip;
